// src/components/hall-schemes/HallPricesModal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Field from "../ui/Field";
import {
    HallScheme,
    SchemeSector,
    SchemeSeat,
    SchemeStandingPlace,
    SchemeTextLabel,
    SchemeShape,
    ScaleValue,
} from "../hall-schemes/HallSchemeEditorCanvas";
import HallPricesCanvas from "../hall-prices/HallPricesCanvas";

import {
    getHallSchemeElementsRequest,
    getHallSchemeRequest,
    getHallSchemeSectionsRequest,
} from "../../services/api/hallSchemes.api";
import {
    HallPriceCategoryItem,
    HallSeatPriceBulkItem,
} from "../../services/types/hallPrices.types";
import {
    getHallPriceCategoriesRequest,
    getHallSeatPricesRequest,
    bulkUpsertHallSeatPricesRequest,
    createHallPriceCategoryRequest,
    deleteHallPriceCategoryRequest,
    updateHallPriceCategoryRequest,
    importTourCitySeatPricesXlsxRequest,
    splitStandingSeatPriceRequest
} from "../../services/api/hallPrices.api";
import CreateAdminReservationModal from "./CreateAdminReservationModal";
import CreateAdminOrderModal from "./CreateAdminOrderModal";
import StandingQuantityModal from "./StandingQuantityModal";
import AdminReservationsManagerModal from "./AdminReservationsManagerModal";
import { getOrderRequest } from "../../services/api/orders.api";
import { getAdminReservationRequest } from "../../services/api/adminReservations.api";

export type BlockedSeatInfo = {
    kind: "order" | "admin_reservation" | "partner";
    loading?: boolean;
    error?: string;
    // order
    orderNumber?: string;
    customerName?: string;
    customerEmail?: string;
    createdByName?: string;
    createdByRole?: string;
    apiDistributorName?: string;
    isApiOrder?: boolean;
    isAgent?: boolean;
    isPaid?: boolean;
    isPaidByDistributor?: boolean;
    isDeleted?: boolean;
    // причина блокировки у партнёра (продано/забронировано/нет цены) — берётся из локальных данных, без запроса.
    message?: string;
    // admin reservation
    reservationName?: string;
    reservationCreatorName?: string;
    reservationUserName?: string;
};

type HallPricesModalProps = {
    isOpen: boolean;
    hallName?: string;
    scheme: HallScheme | null;
    tourCityId: string | null;
    tourId: string | null;
    // organizer_id тура — нужен, чтобы в модалке создания заказа понять,
    // доступны ли скидки по билетам ("rabatt") для этого мероприятия.
    tourOrganizerId?: string | null;
    // ИД сеанса у партнёра (Artist Production) для этого города тура — если задан,
    // модалка работает в режиме партнёрского зала (цены/статусы из их API, свой
    // флоу заказа, админ-брони и ручное управление ценами недоступны).
    partnerSeanceId?: number | null;
    onClose: () => void;
    onSave?: (payload: HallScheme) => void;
};

type EditableSeatPrice = {
    id?: string;
    hall_scheme_element_id: string;
    price_id: string;
    count?: number;
    status?: string;
    admin_reservation_id?: string | null;
    order_id?: string | null;
};

const HallPricesModal: React.FC<HallPricesModalProps> = ({
                                                             isOpen,
                                                             hallName,
                                                             scheme,
                                                             tourCityId,
                                                             tourId,
                                                             tourOrganizerId,
                                                             partnerSeanceId,
                                                             onClose,
                                                             onSave,
                                                         }) => {
    // Режим партнёрского тура (Artist Production): цены/брони управляются через
    // их API, админу недоступны ручные инструменты управления ценами/админ-броня.
    const isPartnerTour = partnerSeanceId !== null && partnerSeanceId !== undefined;
    const [schemeName, setSchemeName] = useState(scheme?.name ?? "");
    const [scale, setScale] = useState<ScaleValue>(1);

    const [sectors, setSectors] = useState<SchemeSector[]>([]);
    const [seats, setSeats] = useState<SchemeSeat[]>([]);
    const [selectedSeatIds, setSelectedSeatIds] = useState<string[]>([]);

    const [standingPlaces, setStandingPlaces] = useState<SchemeStandingPlace[]>([]);
    const [selectedStandingPlaceId, setSelectedStandingPlaceId] = useState<string | null>(
        null,
    );

    const [textLabels, setTextLabels] = useState<SchemeTextLabel[]>([]);
    const [selectedTextLabelId, setSelectedTextLabelId] = useState<string | null>(null);

    const [shapes, setShapes] = useState<SchemeShape[]>([]);
    const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);

    const [priceCategories, setPriceCategories] = useState<HallPriceCategoryItem[]>([]);
    const [selectedPriceCategoryId, setSelectedPriceCategoryId] = useState<string | null>(
        null,
    );
    const [editingPriceValue, setEditingPriceValue] = useState("");
    const [editingPriceColor, setEditingPriceColor] = useState("#6d7cff");
    const [seatPrices, setSeatPrices] = useState<EditableSeatPrice[]>([]);

    const [newPriceValue, setNewPriceValue] = useState("");
    const [newPriceColor, setNewPriceColor] = useState("#6d7cff");

    const [isLoadingScheme, setIsLoadingScheme] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    type PricesPanelMode = "select" | "lasso" | "price-categories";

    const [panelMode, setPanelMode] = useState<PricesPanelMode>("select");

    const importFileInputRef = useRef<HTMLInputElement | null>(null);
    const [isImportingPrices, setIsImportingPrices] = useState(false);

    // Модалки адм. брони
    const [isCreateReservationOpen, setIsCreateReservationOpen] = useState(false);
    const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
    const [isManagerOpen, setIsManagerOpen] = useState(false);

    const title = useMemo(() => {
        if (hallName) {
            return `Редактор цен — ${hallName}`;
        }

        return "Редактор цен";
    }, [hallName]);

    const totalAssignedPrice = useMemo(() => {
        const priceMap = new Map(
            priceCategories.map((category) => [category.id, Number(category.price) || 0]),
        );

        return seatPrices.reduce((sum, item) => {
            const categoryPrice = item.price_id ? priceMap.get(item.price_id) ?? 0 : 0;
            const count = item.count ?? 1;
            return sum + categoryPrice * count;
        }, 0);
    }, [priceCategories, seatPrices]);

    useEffect(() => {
        setSchemeName(scheme?.name ?? "");
    }, [scheme?.id, scheme?.name]);

    const excelTemplateUrl = useMemo(() => {
        if (!scheme?.hall_id || !tourId) {
            return null;
        }

        return `https://api.showmust.de/export/hall/${scheme.hall_id}/tour/${tourId}/seats.xlsx`;
    }, [scheme?.hall_id, tourId]);

    const selectedStandingPlace = useMemo(() => {
        if (!selectedStandingPlaceId) {
            return null;
        }

        return standingPlaces.find((place) => place.id === selectedStandingPlaceId) ?? null;
    }, [standingPlaces, selectedStandingPlaceId]);

    const selectedStandingPlacePrice = useMemo(() => {
        if (!selectedStandingPlaceId) {
            return null;
        }

        return (
            seatPrices.find((item) => item.hall_scheme_element_id === selectedStandingPlaceId) ?? null
        );
    }, [seatPrices, selectedStandingPlaceId]);

    const [standingPlaceCount, setStandingPlaceCount] = useState("1");

    useEffect(() => {
        if (!selectedStandingPlacePrice) {
            setStandingPlaceCount("1");
            return;
        }

        setStandingPlaceCount(String(selectedStandingPlacePrice.count ?? 1));
    }, [selectedStandingPlacePrice]);

    // Строка HallSeatPrice стоячего блока, которая СЕЙЧАС действительно свободна
    // для продажи (order_id не задан). Не переиспользуем для этого
    // selectedStandingPlacePrice выше — он берёт первую строку по порядку создания,
    // что после разделения блока (split) могло бы ошибочно вернуть уже
    // проданную (order_id задан) старую строку вместо свежего остатка.
    const currentSellableStandingSeatPrice = useMemo(() => {
        if (!selectedStandingPlaceId) {
            return null;
        }

        const candidates = seatPrices.filter(
            (item) => item.hall_scheme_element_id === selectedStandingPlaceId && item.order_id == null,
        );

        if (candidates.length === 0) {
            return null;
        }

        return candidates.reduce((best, item) => ((item.count ?? 0) > (best.count ?? 0) ? item : best));
    }, [seatPrices, selectedStandingPlaceId]);

    // Выбранное количество стоячих мест для ТЕКУЩЕГО заказа (после того как
    // админ подтвердил количество в StandingQuantityModal и бэкенд успешно разделил
    // блок). Отличается от selectedStandingPlaceId (какая зона выделена на схеме) —
    // это конкретно та, строка которую надо передать в создание заказа.
    // hallSeatPriceIds теперь МАССИВ из `quantity` отдельных id (по одному на билет) —
    // бэкенд /split возвращает список отдельных строк HallSeatPrice (count=1 каждая),
    // а не одну строку с count=N, иначе в заказ попадал только 1 билет вместо N.
    const [standingOrderSelection, setStandingOrderSelection] = useState<{
        elementId: string;
        hallSeatPriceIds: string[];
        quantity: number;
        label: string;
        unitPrice: number;
    } | null>(null);

    const [isStandingQuantityOpen, setIsStandingQuantityOpen] = useState(false);
    const [isSplittingStandingPrice, setIsSplittingStandingPrice] = useState(false);
    const [standingSplitError, setStandingSplitError] = useState<string | null>(null);

    // При смене выделенной зоны сбрасываем ранее подтверждённое количество —
    // оно относится только к предыдущей выбранной зоне.
    useEffect(() => {
        setStandingOrderSelection(null);
        setStandingSplitError(null);
    }, [selectedStandingPlaceId]);

    // Открывается по клику прямо на стоячую зону на схеме (см. onStandingPlaceOrderClick
    // ниже) — админ указывает количество через +/- в StandingQuantityModal, после чего
    // блок делится на бэкенде и сразу открывается форма оформления заказа.
    const handleConfirmStandingQuantity = async (quantity: number) => {
        if (!selectedStandingPlaceId || !currentSellableStandingSeatPrice?.id) {
            return;
        }

        try {
            setIsSplittingStandingPrice(true);
            setStandingSplitError(null);

            // Бэкенд возвращает список из `quantity` отдельных строк (по одной на билет).
            const results = await splitStandingSeatPriceRequest(
                currentSellableStandingSeatPrice.id,
                quantity,
            );

            const priceById = new Map(priceCategories.map((c) => [c.id, Number(c.price) || 0]));
            const firstPriceId = results[0]?.price_id;
            const unitPrice = firstPriceId ? priceById.get(firstPriceId) ?? 0 : 0;
            const place = standingPlaces.find((p) => p.id === selectedStandingPlaceId);

            setStandingOrderSelection({
                elementId: selectedStandingPlaceId,
                hallSeatPriceIds: results.map((r) => r.id),
                quantity,
                label: place?.text ? `стоячее место: ${place.text}` : "стоячее место",
                unitPrice,
            });

            // Обновляем список цен/мест, чтобы на схеме сразу было видно обновлённое оставшееся
            // количество, а новая строка была доступна дальше по id.
            await reloadTourCityPrices();

            setIsStandingQuantityOpen(false);
            setIsCreateOrderOpen(true);
        } catch (error) {
            setStandingSplitError(
                error instanceof Error ? error.message : "Не удалось выделить стоячие места для заказа.",
            );
        } finally {
            setIsSplittingStandingPrice(false);
        }
    };

    // Клик прямо по стоячей зоне на схеме сразу открывает окно выбора количества —
    // отдельная кнопка «Сделать заказ» для стоячих зон больше не нужна.
    const handleStandingPlaceOrderClick = (placeId: string) => {
        setSelectedStandingPlaceId(placeId);
        setStandingSplitError(null);
        setIsStandingQuantityOpen(true);
    };


    useEffect(() => {
        if (!isOpen || !scheme?.id) {
            return;
        }

        let isCancelled = false;

        setIsLoadingScheme(true);
        setLoadError(null);
        setSaveError(null);
        setSaveSuccess(null);

        setSectors([]);
        setSeats([]);
        setStandingPlaces([]);
        setTextLabels([]);
        setShapes([]);
        setSelectedSeatIds([]);
        setSelectedStandingPlaceId(null);
        setSelectedTextLabelId(null);
        setSelectedShapeId(null);

        setPriceCategories([]);
        setSeatPrices([]);
        setSelectedPriceCategoryId(null);

        const loadData = async () => {
            setIsLoadingScheme(true);
            setLoadError(null);
            setSaveError(null);
            setSaveSuccess(null);

            try {
                const [schemeResponse, sectionsResponse, elementsResponse] = await Promise.all([
                    getHallSchemeRequest(scheme.id),
                    getHallSchemeSectionsRequest(scheme.id),
                    getHallSchemeElementsRequest(scheme.id),
                ]);

                if (isCancelled) {
                    return;
                }

                setSchemeName(schemeResponse.name ?? "");

                const nextSectors: SchemeSector[] = (sectionsResponse ?? [])
                    .filter((item) => item.is_active !== false)
                    .map((item) => ({
                        id: item.id,
                        name: item.name,
                        color: item.color ?? "#6d7cff",
                    }));

                const nextSeats: SchemeSeat[] = [];
                const nextStandingPlaces: SchemeStandingPlace[] = [];
                const nextTextLabels: SchemeTextLabel[] = [];
                const nextShapes: SchemeShape[] = [];

                elementsResponse
                    .filter((item) => item.is_active !== false)
                    .forEach((item) => {
                        if (item.type === "seat") {
                            nextSeats.push({
                                id:
                                    item.id ??
                                    `seat-${Date.now()}-${Math.random()
                                        .toString(16)
                                        .slice(2)}`,
                                seatNumber: String(item.seat_number ?? 0),
                                rowNumber: String(item.row_number ?? 0),
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                rowType:
                                    item.row_type === "vertical" ? "vertical" : "horizontal",
                                sectorId: item.sector_id ?? null,
                                rowGroupId:
                                    item.row_group_id ??
                                    `row-${Date.now()}-${Math.random()
                                        .toString(16)
                                        .slice(2)}`,
                            });
                        }

                        if (item.type === "standing-place") {
                            nextStandingPlaces.push({
                                id:
                                    item.id ??
                                    `standing-${Date.now()}-${Math.random()
                                        .toString(16)
                                        .slice(2)}`,
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                width: item.width ?? 0,
                                height: item.height ?? 0,
                                text: item.text ?? "",
                                sectorId: item.sector_id ?? null,
                            });
                        }

                        if (item.type === "text") {
                            nextTextLabels.push({
                                id:
                                    item.id ??
                                    `text-${Date.now()}-${Math.random()
                                        .toString(16)
                                        .slice(2)}`,
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                width: item.width ?? 120,
                                height: item.height ?? 32,
                                text: item.text ?? "",
                            });
                        }

                        if (item.type === "shape") {
                            nextShapes.push({
                                id:
                                    item.id ??
                                    `shape-${Date.now()}-${Math.random()
                                        .toString(16)
                                        .slice(2)}`,
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                width: item.width ?? 120,
                                height: item.height ?? 80,
                                shapeType: item.shape_type ?? "rectangle",
                                borderRadius: Number(item.border_radius ?? 12),
                                isOval: Boolean(item.isOval),
                            });
                        }
                    });

                setSectors(nextSectors);
                setSeats(nextSeats);
                setStandingPlaces(nextStandingPlaces);
                setTextLabels(nextTextLabels);
                setShapes(nextShapes);

                if (tourCityId) {
                    await reloadTourCityPrices();

                    if (isCancelled) return;
                }
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                setLoadError(
                    error instanceof Error
                        ? error.message
                        : "Не удалось загрузить данные.",
                );
            } finally {
                if (!isCancelled) {
                    setIsLoadingScheme(false);
                }
            }
        };

        void loadData();

        return () => {
            isCancelled = true;
        };
    }, [isOpen, scheme?.id, tourCityId]);

    const selectedPriceCategory = useMemo(() => {
        if (!selectedPriceCategoryId) {
            return null;
        }

        return (
            priceCategories.find((item) => item.id === selectedPriceCategoryId) ?? null
        );
    }, [priceCategories, selectedPriceCategoryId]);

    const selectedSeatsList = useMemo(() => {
        return seats.filter((seat) => selectedSeatIds.includes(seat.id));
    }, [seats, selectedSeatIds]);

    // Сводка по выбранным билетам с человеческим описанием и ценой — для модалки создания
    // админ-заказа, где нужно назначать скидку каждому билету отдельно.
    const selectedSeatsInfo = useMemo(() => {
        const priceById = new Map(priceCategories.map((c) => [c.id, Number(c.price) || 0]));
        const seatPriceByElementId = new Map(
            seatPrices.map((sp) => [sp.hall_scheme_element_id, sp]),
        );
        const sectorNameById = new Map(sectors.map((s) => [s.id, s.name]));

        const result: { hall_seat_price_id: string; label: string; price: number }[] = [];

        selectedSeatsList.forEach((seat) => {
            const sp = seatPriceByElementId.get(seat.id);
            if (!sp?.id) return;
            const price = sp.price_id ? priceById.get(sp.price_id) ?? 0 : 0;
            const sectorName = seat.sectorId ? sectorNameById.get(seat.sectorId) : null;
            const label = [sectorName, `ряд ${seat.rowNumber}`, `место ${seat.seatNumber}`]
                .filter(Boolean)
                .join(", ");
            result.push({ hall_seat_price_id: sp.id, label, price });
        });

        if (selectedStandingPlaceId) {
            if (standingOrderSelection && standingOrderSelection.elementId === selectedStandingPlaceId) {
                // Количество уже подтверждено в StandingQuantityModal, блок уже разделён на
                // `quantity` ОТДЕЛЬНЫХ строк (по одной на билет) — добавляем отдельную строку
                // в список на каждый id, чтобы в заказ ушли ВСЕ билеты, а не только один
                // (иначе создавался только 1 билет вместо N).
                standingOrderSelection.hallSeatPriceIds.forEach((id, index) => {
                    const suffix =
                        standingOrderSelection.quantity > 1
                            ? ` (${index + 1} из ${standingOrderSelection.quantity})`
                            : "";
                    result.push({
                        hall_seat_price_id: id,
                        label: `${standingOrderSelection.label}${suffix}`,
                        price: standingOrderSelection.unitPrice,
                    });
                });
            } else {
                const sp = seatPriceByElementId.get(selectedStandingPlaceId);
                if (sp?.id) {
                    const price = sp.price_id ? priceById.get(sp.price_id) ?? 0 : 0;
                    const place = standingPlaces.find((p) => p.id === selectedStandingPlaceId);
                    const label = place?.text ? `стоячее место: ${place.text}` : "стоячее место";
                    result.push({ hall_seat_price_id: sp.id, label, price });
                }
            }
        }

        return result;
    }, [
        selectedSeatsList,
        selectedStandingPlaceId,
        seatPrices,
        priceCategories,
        sectors,
        standingPlaces,
        standingOrderSelection,
    ]);

    // Список hall_scheme_element_id, которые заблокированы (заняты заказом или адм. бронью).
    // Для партнёрских залов (Artist Production) дополнительно блокируем места без
    // назначенной цены — значит, партнёр не отдал его в продажу и выбрать его нельзя.
    //
    // У стоячих зон после разделения (split) может быть НЕСКОЛЬКО строк hall_seat_prices
    // на один элемент: часть продана (order_id заполнен), а остаток остаётся отдельной
    // свободной строкой. Поэтому для стоячих зон элемент считается заблокированным, только
    // если ВСЕ его строки заняты — иначе зона с ещё доступным остатком некорректно
    // помечалась бы как полностью занятая после первой же частичной продажи.
    const standingElementIdSet = useMemo(
        () => new Set(standingPlaces.map((place) => place.id)),
        [standingPlaces],
    );

    const blockedElementIds = useMemo(() => {
        const isRowOccupied = (sp: EditableSeatPrice) => {
            const status = sp.status;
            const orderId = sp.order_id;
            const adminResId = sp.admin_reservation_id;
            const hasNoPrice = !sp.price_id;
            return Boolean(
                status === "sold" ||
                    status === "admin" ||
                    status === "hold" ||
                    status === "reserved" ||
                    status === "blocked" ||
                    orderId ||
                    adminResId ||
                    (isPartnerTour && hasNoPrice),
            );
        };

        const rowsByElementId = new Map<string, EditableSeatPrice[]>();
        seatPrices.forEach((sp) => {
            const list = rowsByElementId.get(sp.hall_scheme_element_id);
            if (list) {
                list.push(sp as EditableSeatPrice);
            } else {
                rowsByElementId.set(sp.hall_scheme_element_id, [sp as EditableSeatPrice]);
            }
        });

        const set = new Set<string>();
        rowsByElementId.forEach((rows, elementId) => {
            const isBlocked = standingElementIdSet.has(elementId)
                ? rows.every(isRowOccupied)
                : rows.some(isRowOccupied);
            if (isBlocked) {
                set.add(elementId);
            }
        });
        return set;
    }, [seatPrices, isPartnerTour, standingElementIdSet]);

    // Причина блокировки места у партнёра (когда нет локального заказа/адм.брони — она взята из
    // статуса/цены, полученных при синхронизации с API партнёра) — без сетевого запроса.
    const partnerBlockReasonByElementId = useMemo(() => {
        const map = new Map<string, string>();
        if (!isPartnerTour) return map;
        seatPrices.forEach((sp) => {
            const status = (sp as EditableSeatPrice).status;
            const orderId = (sp as EditableSeatPrice).order_id;
            const adminResId = (sp as EditableSeatPrice).admin_reservation_id;
            // Если есть наш собственный заказ/бронь — подробности покажет другая ветка (blockedSourceByElementId).
            if (orderId || adminResId) return;
            if (status === "reserved" || status === "sold") {
                map.set(sp.hall_scheme_element_id, "Продано у партнёра");
            } else if (status === "hold") {
                map.set(sp.hall_scheme_element_id, "Забронировано у партнёра");
            } else if (!sp.price_id) {
                map.set(sp.hall_scheme_element_id, "Нет цены у партнёра — место не в продаже");
            }
        });
        return map;
    }, [seatPrices, isPartnerTour]);

    // Сопоставление hall_scheme_element_id -> ссылка на источник блокировки (заказ / адм. бронь).
    // Используется для подсказки при наведении на занятое место.
    const blockedSourceByElementId = useMemo(() => {
        const map = new Map<
            string,
            { orderId?: string | null; adminReservationId?: string | null }
        >();
        seatPrices.forEach((sp) => {
            const orderId = (sp as EditableSeatPrice).order_id;
            const adminResId = (sp as EditableSeatPrice).admin_reservation_id;
            if (orderId || adminResId) {
                map.set(sp.hall_scheme_element_id, {
                    orderId: orderId ?? null,
                    adminReservationId: adminResId ?? null,
                });
            }
        });
        return map;
    }, [seatPrices]);

    // Кеш деталей по ключу "order:<id>" / "reservation:<id>".
    const [blockedInfoCache, setBlockedInfoCache] = useState<Record<string, BlockedSeatInfo>>({});
    const inFlightRef = useRef<Set<string>>(new Set());

    const formatPersonName = (
        first?: string | null,
        last?: string | null,
        email?: string | null,
    ) => {
        const parts = [first, last].filter((v) => v && String(v).trim());
        if (parts.length) return parts.join(" ").trim();
        if (email) return email;
        return "";
    };

    const loadBlockedInfoForElement = React.useCallback(
        (elementId: string) => {
            const ref = blockedSourceByElementId.get(elementId);
            if (!ref) return;

            if (ref.orderId) {
                const cacheKey = `order:${ref.orderId}`;
                if (blockedInfoCache[cacheKey] || inFlightRef.current.has(cacheKey)) return;
                inFlightRef.current.add(cacheKey);
                setBlockedInfoCache((prev) => ({
                    ...prev,
                    [cacheKey]: { kind: "order", loading: true },
                }));
                getOrderRequest(ref.orderId)
                    .then((order) => {
                        const customerName = formatPersonName(
                            order.customer_info?.first_name,
                            order.customer_info?.last_name,
                            order.customer_info?.email,
                        );
                        const createdByName = formatPersonName(
                            order.created_by_info?.first_name,
                            order.created_by_info?.last_name,
                            order.created_by_info?.email,
                        );
                        setBlockedInfoCache((prev) => ({
                            ...prev,
                            [cacheKey]: {
                                kind: "order",
                                orderNumber: order.order_number,
                                customerName,
                                customerEmail: order.customer_info?.email ?? undefined,
                                createdByName,
                                createdByRole: order.created_by_info?.role ?? undefined,
                                apiDistributorName:
                                    (order as unknown as {
                                        api_distributor_info?: { name?: string | null } | null;
                                    }).api_distributor_info?.name ?? undefined,
                                isApiOrder: Boolean(
                                    (order as unknown as { is_api_order?: boolean })
                                        .is_api_order,
                                ),
                                isAgent: Boolean(order.customer_info?.is_agent),
                                isPaid: Boolean(
                                    (order as unknown as { is_paid?: boolean }).is_paid,
                                ),
                                isPaidByDistributor: Boolean(
                                    (order as unknown as { is_paid_by_distributor?: boolean })
                                        .is_paid_by_distributor,
                                ),
                                isDeleted: Boolean(
                                    (order as unknown as { is_deleted?: boolean }).is_deleted,
                                ),
                            },
                        }));
                    })
                    .catch((error) => {
                        setBlockedInfoCache((prev) => ({
                            ...prev,
                            [cacheKey]: {
                                kind: "order",
                                error:
                                    error instanceof Error
                                        ? error.message
                                        : "Не удалось загрузить заказ",
                            },
                        }));
                    })
                    .finally(() => {
                        inFlightRef.current.delete(cacheKey);
                    });
                return;
            }

            if (ref.adminReservationId) {
                const cacheKey = `reservation:${ref.adminReservationId}`;
                if (blockedInfoCache[cacheKey] || inFlightRef.current.has(cacheKey)) return;
                inFlightRef.current.add(cacheKey);
                setBlockedInfoCache((prev) => ({
                    ...prev,
                    [cacheKey]: { kind: "admin_reservation", loading: true },
                }));
                getAdminReservationRequest(ref.adminReservationId)
                    .then((res) => {
                        setBlockedInfoCache((prev) => ({
                            ...prev,
                            [cacheKey]: {
                                kind: "admin_reservation",
                                reservationName: res.name,
                                reservationUserName: formatPersonName(
                                    res.user?.first_name,
                                    res.user?.last_name,
                                    res.user?.email,
                                ),
                                reservationCreatorName: formatPersonName(
                                    res.creator?.first_name,
                                    res.creator?.last_name,
                                    res.creator?.email,
                                ),
                            },
                        }));
                    })
                    .catch((error) => {
                        setBlockedInfoCache((prev) => ({
                            ...prev,
                            [cacheKey]: {
                                kind: "admin_reservation",
                                error:
                                    error instanceof Error
                                        ? error.message
                                        : "Не удалось загрузить бронь",
                            },
                        }));
                    })
                    .finally(() => {
                        inFlightRef.current.delete(cacheKey);
                    });
            }
        },
        [blockedSourceByElementId, blockedInfoCache],
    );

    const getBlockedInfoForElement = React.useCallback(
        (elementId: string): BlockedSeatInfo | null => {
            const ref = blockedSourceByElementId.get(elementId);
            if (!ref) {
                const partnerReason = partnerBlockReasonByElementId.get(elementId);
                if (partnerReason) {
                    return { kind: "partner", message: partnerReason };
                }
                return null;
            }
            const key = ref.orderId
                ? `order:${ref.orderId}`
                : ref.adminReservationId
                    ? `reservation:${ref.adminReservationId}`
                    : null;
            if (!key) return null;
            return blockedInfoCache[key] ?? null;
        },
        [blockedSourceByElementId, blockedInfoCache, partnerBlockReasonByElementId],
    );

    // Сброс кеша при перезагрузке цен (например, после сохранения)
    useEffect(() => {
        setBlockedInfoCache({});
        inFlightRef.current = new Set();
    }, [tourCityId]);

    // Сопоставление hall_scheme_element_id -> id записи hall_seat_prices
    const seatPriceIdByElementId = useMemo(() => {
        const map = new Map<string, string>();
        seatPrices.forEach((sp) => {
            if (sp.id) {
                map.set(sp.hall_scheme_element_id, sp.id);
            }
        });
        return map;
    }, [seatPrices]);

    const selectedSeatPriceIds = useMemo(() => {
        const result: string[] = [];
        selectedSeatIds.forEach((seatId) => {
            const id = seatPriceIdByElementId.get(seatId);
            if (id) result.push(id);
        });
        // Стоячие зоны тоже включаем, если выбрана именно она. Если админ уже
        // подтвердил количество через StandingQuantityModal (блок уже разделён) —
        // берём id именно той (разделённой) строки, а не произвольной из общей мапы.
        if (selectedStandingPlaceId) {
            if (standingOrderSelection && standingOrderSelection.elementId === selectedStandingPlaceId) {
                result.push(...standingOrderSelection.hallSeatPriceIds);
            } else {
                const id = seatPriceIdByElementId.get(selectedStandingPlaceId);
                if (id) result.push(id);
            }
        }
        return result;
    }, [selectedSeatIds, selectedStandingPlaceId, seatPriceIdByElementId, standingOrderSelection]);

    const hasSelectedSchemeElements =
        selectedSeatIds.length > 0 || selectedStandingPlaceId !== null;

    const handleCreatePriceCategory = async () => {
        if (!tourCityId) {
            return;
        }

        const raw = newPriceValue.trim().replace(",", ".");
        const priceNumber = Number(raw);

        if (!Number.isFinite(priceNumber) || priceNumber <= 0) {
            setSaveError("Введите корректную цену.");
            return;
        }

        try {
            setIsSaving(true);
            setSaveError(null);
            setSaveSuccess(null);

            const payload = {
                tour_city_id: tourCityId,
                price: priceNumber,
                color: newPriceColor,
            };

            const created = await createHallPriceCategoryRequest(payload);

            setPriceCategories((prev) => [...prev, created]);
            setSelectedPriceCategoryId(created.id);
            setNewPriceValue("");
            setSaveSuccess("Категория цены создана.");
        } catch (error) {
            setSaveError(
                error instanceof Error
                    ? error.message
                    : "Не удалось создать категорию цены.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeletePriceCategory = async () => {
        if (!selectedPriceCategoryId) {
            return;
        }

        try {
            setIsSaving(true);
            setSaveError(null);
            setSaveSuccess(null);

            await deleteHallPriceCategoryRequest(selectedPriceCategoryId);

            setPriceCategories((prev) =>
                prev.filter((item) => item.id !== selectedPriceCategoryId),
            );

            setSeatPrices((prev) =>
                prev.filter((item) => item.price_id !== selectedPriceCategoryId),
            );

            setSelectedPriceCategoryId(null);
            setSaveSuccess("Категория цены удалена.");
        } catch (error) {
            setSaveError(
                error instanceof Error
                    ? error.message
                    : "Не удалось удалить категорию цены.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleAssignSelectedSeatsToPrice = () => {
        if (!selectedPriceCategoryId || selectedSeatIds.length === 0) {
            return;
        }

        setSaveError(null);
        setSaveSuccess(null);

        setSeatPrices((prev) => {
            const next = [...prev];

            selectedSeatIds.forEach((seatId) => {
                const existingIndex = next.findIndex(
                    (item) => item.hall_scheme_element_id === seatId,
                );

                if (existingIndex >= 0) {
                    next[existingIndex] = {
                        ...next[existingIndex],
                        price_id: selectedPriceCategoryId,
                    };
                } else {
                    next.push({
                        id: "",
                        hall_scheme_element_id: seatId,
                        price_id: selectedPriceCategoryId,
                        count: 1,
                    });
                }
            });

            return next;
        });

        setSaveSuccess("Цена назначена локально. Нажмите «Сохранить» для отправки изменений.");
    };

    const handleAssignSelectionToPrice = () => {
        // Выбор может включать одновременно и обычные места, и стоячую зону — назначаем
        // цену всем выбранным элементам за один вызов, а не только одному из двух типов.
        if (!selectedPriceCategoryId || (selectedSeatIds.length === 0 && !selectedStandingPlaceId)) {
            return;
        }

        setSaveError(null);
        setSaveSuccess(null);

        setSeatPrices((prev) => {
            const next = [...prev];

            selectedSeatIds.forEach((seatId) => {
                const existingIndex = next.findIndex(
                    (item) => item.hall_scheme_element_id === seatId,
                );

                if (existingIndex >= 0) {
                    next[existingIndex] = {
                        ...next[existingIndex],
                        price_id: selectedPriceCategoryId,
                        count: 1,
                    };
                } else {
                    next.push({
                        id: "",
                        hall_scheme_element_id: seatId,
                        price_id: selectedPriceCategoryId,
                        count: 1,
                    });
                }
            });

            if (selectedStandingPlaceId) {
                const existingIndex = next.findIndex(
                    (item) => item.hall_scheme_element_id === selectedStandingPlaceId,
                );

                if (existingIndex >= 0) {
                    // Для залов, синхронизированных с партнёрским API, количество приходит
                    // от партнёра — игнорируем то, что вручную введено в поле «Количество в
                    // зоне» (оно для таких залов даже не отображается), и меняем только цену.
                    const normalizedCount = isPartnerTour
                        ? next[existingIndex].count
                        : Math.max(1, Math.trunc(Number(standingPlaceCount) || 1));

                    next[existingIndex] = {
                        ...next[existingIndex],
                        price_id: selectedPriceCategoryId,
                        count: normalizedCount,
                    };
                } else {
                    const normalizedCount = isPartnerTour
                        ? 1
                        : Math.max(1, Math.trunc(Number(standingPlaceCount) || 1));

                    next.push({
                        id: "",
                        hall_scheme_element_id: selectedStandingPlaceId,
                        price_id: selectedPriceCategoryId,
                        count: normalizedCount,
                    });
                }
            }

            return next;
        });

        setSaveSuccess("Цена назначена локально. Нажмите «Сохранить» для отправки изменений.");
    };

    const handleSave = async () => {
        if (!scheme || !tourCityId) {
            return;
        }

        try {
            setIsSaving(true);
            setSaveError(null);
            setSaveSuccess(null);

            const items: HallSeatPriceBulkItem[] = seatPrices.map((item) => ({
                hall_seat_price_id: item.id ?? "",
                price_id: item.price_id,
                count: item.count ?? 1,
            }));


            const savedSeatPrices = await bulkUpsertHallSeatPricesRequest({ items });

            setSeatPrices((savedSeatPrices ?? []) as EditableSeatPrice[]);

            const payload: HallScheme = {
                id: scheme.id,
                hall_id: scheme.hall_id,
                name: schemeName.trim() || scheme.name,
            };

            onSave?.(payload);

            setSaveSuccess("Изменения цен сохранены.");
        } catch (error) {
            setSaveError(
                error instanceof Error
                    ? error.message
                    : "Не удалось сохранить изменения цен.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (!selectedPriceCategory) {
            setEditingPriceColor("#6d7cff");
            return;
        }

        setEditingPriceColor(selectedPriceCategory.color ?? "#6d7cff");
    }, [selectedPriceCategory]);

    const handleUpdatePriceCategoryColor = async () => {
        if (!selectedPriceCategoryId) {
            return;
        }

        try {
            setIsSaving(true);
            setSaveError(null);
            setSaveSuccess(null);

            const updated = await updateHallPriceCategoryRequest(selectedPriceCategoryId, {
                color: editingPriceColor,
            });

            setPriceCategories((prev) =>
                prev.map((item) =>
                    item.id === selectedPriceCategoryId ? updated : item
                ),
            );

            setSaveSuccess("Цвет категории обновлён.");
        } catch (error) {
            setSaveError(
                error instanceof Error
                    ? error.message
                    : "Не удалось обновить цвет категории.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const reloadTourCityPrices = async () => {
        if (!tourCityId) {
            setPriceCategories([]);
            setSeatPrices([]);
            setSelectedPriceCategoryId(null);
            return;
        }

        const [priceCategoriesResponse, seatPricesResponse] = await Promise.all([
            getHallPriceCategoriesRequest(tourCityId),
            getHallSeatPricesRequest(tourCityId),
        ]);

        setPriceCategories(priceCategoriesResponse ?? []);
        setSeatPrices((seatPricesResponse ?? []) as EditableSeatPrice[]);
        console.log(seatPricesResponse)
    };

    const handleImportPricesClick = () => {
        if (!tourCityId || isImportingPrices || isLoadingScheme) {
            return;
        }

        importFileInputRef.current?.click();
    };

    const handleImportPricesFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];

        event.currentTarget.value = "";

        if (!file || !tourCityId) {
            return;
        }

        try {
            setIsImportingPrices(true);
            setSaveError(null);
            setSaveSuccess(null);

            await importTourCitySeatPricesXlsxRequest(tourCityId, file);
            await reloadTourCityPrices();

            setSaveSuccess("Цены успешно импортированы из Excel.");
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : "Не удалось импортировать цены.");
        } finally {
            setIsImportingPrices(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="scheme-editor-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Редактор цен зала"
        >
            <div className="scheme-editor-modal-content">
                <header className="scheme-editor-modal-header">
                    <div className="scheme-editor-header-main">
                        <p className="scheme-editor-header-eyebrow">Hall Prices Editor</p>
                        <h2 className="scheme-editor-header-title">{title}</h2>
                    </div>

                    <div className="scheme-editor-header-actions">
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Закрыть
                        </Button>

                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={!scheme || isSaving || isLoadingScheme}
                        >
                            {isSaving ? "Сохранение..." : "Сохранить"}
                        </Button>
                    </div>
                </header>

                <div className="scheme-editor-modal-body">
                    <aside className="scheme-editor-tools">
                        <section className="scheme-editor-section">
                            <h3 className="scheme-editor-section-title">Информация о схеме</h3>

                            <div className="scheme-editor-info-list">
                                <div className="scheme-editor-info-item">
                                    <span className="scheme-editor-info-label">Название</span>
                                    <span className="scheme-editor-info-value">
                                        {schemeName || scheme?.name || "Без названия"}
                                    </span>
                                </div>

                                <div className="scheme-editor-info-item">
                                    <span className="scheme-editor-info-label">Общая сумма</span>
                                    <span className="scheme-editor-info-value">
                                        {Math.round(totalAssignedPrice)} €
                                    </span>
                                </div>
                            </div>

                            {isLoadingScheme ? (
                                <p className="scheme-editor-tool-properties-hint">
                                    Загрузка схемы...
                                </p>
                            ) : null}

                            {loadError ? (
                                <p
                                    className="scheme-editor-tool-properties-hint"
                                    style={{ color: "#ef4444" }}
                                >
                                    {loadError}
                                </p>
                            ) : null}

                            {saveError ? (
                                <p
                                    className="scheme-editor-tool-properties-hint"
                                    style={{ color: "#ef4444" }}
                                >
                                    {saveError}
                                </p>
                            ) : null}

                            {saveSuccess ? (
                                <p
                                    className="scheme-editor-tool-properties-hint"
                                    style={{ color: "#16a34a" }}
                                >
                                    {saveSuccess}
                                </p>
                            ) : null}
                        </section>

                        {panelMode === "price-categories" ? (
                        <section className="scheme-editor-section">
                            <h3 className="scheme-editor-section-title">
                                Добавить категорию цены
                            </h3>

                            <Field label="Цена" htmlFor="new-price-value">
                                <Input
                                    id="new-price-value"
                                    value={newPriceValue}
                                    onChange={(event) => setNewPriceValue(event.target.value)}
                                    placeholder="Например, 2500"
                                />
                            </Field>

                            <div className="scheme-editor-color-row">
                                <span className="scheme-editor-info-label">Цвет</span>
                                <input
                                    type="color"
                                    className="scheme-editor-color-input"
                                    value={newPriceColor}
                                    onChange={(event) => setNewPriceColor(event.target.value)}
                                />
                            </div>

                            <div className="editor-actions">
                                <Button
                                    type="button"
                                    onClick={handleCreatePriceCategory}
                                    disabled={!tourCityId || isSaving}
                                >
                                    Создать категорию
                                </Button>
                            </div>
                        </section>
                        ) : null}

                        <section className="scheme-editor-section">
                            <h3 className="scheme-editor-section-title">Категории цен</h3>

                            {priceCategories.length > 0 ? (
                                <div className="scheme-editor-section-list">
                                    {priceCategories.map((category) => (
                                        <button
                                            key={category.id}
                                            type="button"
                                            className={`scheme-editor-section-item ${
                                                selectedPriceCategoryId === category.id
                                                    ? "scheme-editor-section-item-active"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                setSelectedPriceCategoryId(category.id)
                                            }
                                        >
                                            <span
                                                className="scheme-editor-section-swatch"
                                                style={{ backgroundColor: category.color }}
                                            />
                                            <span className="scheme-editor-section-item-text">
                                                {Math.round(Number(category.price))} €
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-block">
                                    <h3 className="empty-block-title">
                                        Категорий цен пока нет
                                    </h3>
                                    <p className="empty-block-text">
                                        Создайте одну или несколько категорий, чтобы
                                        назначать цены местам.
                                    </p>
                                </div>
                            )}
                        </section>

                        {selectedPriceCategory && selectedSeatIds.length === 0 && !hasSelectedSchemeElements ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">
                                    Свойства категории
                                </h3>

                                {/*<Field label="Цена" htmlFor="edit-price-value">*/}
                                {/*    <Input*/}
                                {/*        id="edit-price-value"*/}
                                {/*        value={editingPriceValue}*/}
                                {/*        onChange={(event) => setEditingPriceValue(event.target.value)}*/}
                                {/*        placeholder="Например, 2500"*/}
                                {/*    />*/}
                                {/*</Field>*/}

                                <div className="scheme-editor-color-row">
                                    <span className="scheme-editor-info-label">Цвет</span>
                                    <input
                                        type="color"
                                        className="scheme-editor-color-input"
                                        value={editingPriceColor}
                                        onChange={(event) => setEditingPriceColor(event.target.value)}
                                    />
                                </div>

                                <div className="editor-actions">
                                    <Button
                                        type="button"
                                        onClick={handleUpdatePriceCategoryColor}
                                        disabled={isSaving}
                                    >
                                        Сохранить категорию
                                    </Button>

                                    {/*<Button*/}
                                    {/*    type="button"*/}
                                    {/*    variant="secondary"*/}
                                    {/*    onClick={handleDeletePriceCategory}*/}
                                    {/*    disabled={isSaving}*/}
                                    {/*>*/}
                                    {/*    Удалить категорию*/}
                                    {/*</Button>*/}
                                </div>
                            </section>
                        ) : null}

                        {selectedStandingPlace || selectedSeatIds.length > 0 ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">
                                    Выделение
                                </h3>

                                <div className="scheme-editor-tool-properties">
                                    {selectedSeatIds.length > 0 ? (
                                        <p className="scheme-editor-tool-properties-text">
                                            Выбрано обычных мест: <strong>{selectedSeatIds.length}</strong>
                                        </p>
                                    ) : null}

                                    {selectedStandingPlace ? (
                                        <p className="scheme-editor-tool-properties-text">
                                            Стоячая зона: <strong>{selectedStandingPlace.text || "Без названия"}</strong>
                                        </p>
                                    ) : null}

                                    <p className="scheme-editor-tool-properties-hint">
                                        {selectedSeatIds.length > 0 && selectedStandingPlace
                                            ? "Можно оформить один заказ на обычные места и стоячую зону вместе. Нажмите на стоячую зону на схеме, чтобы указать количество и оформить заказ."
                                            : selectedStandingPlace
                                                ? isPartnerTour
                                                    ? "Выберите категорию цены, затем нажмите на стоячую зону на схеме, чтобы указать количество и оформить заказ."
                                                    : "Выберите категорию цены и, при необходимости, укажите количество мест в зоне. Нажмите на стоячую зону на схеме, чтобы указать количество и оформить заказ."
                                                : "Выберите категорию и нажмите “Назначить цену”."}
                                    </p>
                                </div>

                                {selectedStandingPlace && !isPartnerTour ? (
                                    <Field label="Количество в зоне" htmlFor="standing-place-count">
                                        <Input
                                            id="standing-place-count"
                                            value={standingPlaceCount}
                                            onChange={(event) => setStandingPlaceCount(event.target.value)}
                                            placeholder="Например, 100"
                                        />
                                    </Field>
                                ) : null}

                                <div className="editor-actions" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    <Button
                                        type="button"
                                        onClick={handleAssignSelectionToPrice}
                                        disabled={
                                            !tourCityId ||
                                            !selectedPriceCategoryId ||
                                            (selectedSeatIds.length === 0 && !selectedStandingPlace) ||
                                            isSaving
                                        }
                                    >
                                        Назначить цену
                                    </Button>

                                    {!isPartnerTour && !selectedStandingPlace ? (
                                        // Админ. бронь доступна только для обычных мест — для стоячих зон
                                        // (в т.ч. при смешанном выборе) она не предусмотрена по дизайну.
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => setIsCreateReservationOpen(true)}
                                            disabled={
                                                !tourCityId ||
                                                selectedSeatPriceIds.length === 0 ||
                                                isSaving
                                            }
                                        >
                                            Административная бронь
                                        </Button>
                                    ) : null}

                                    {!selectedStandingPlace ? (
                                        // Для стоячей зоны кнопка не нужна — окно выбора количества теперь
                                        // открывается кликом прямо по зоне на схеме.
                                        <Button
                                            type="button"
                                            variant="primary"
                                            onClick={() => setIsCreateOrderOpen(true)}
                                            disabled={!tourCityId || isSaving || selectedSeatPriceIds.length === 0}
                                        >
                                            Сделать заказ
                                        </Button>
                                    ) : (
                                        <p className="scheme-editor-tool-properties-hint">
                                            Для оформления заказа нажмите на стоячую зону на схеме выше.
                                        </p>
                                    )}
                                </div>
                            </section>
                        ) : (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">
                                    Работа с местами
                                </h3>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Выберите на схеме одно или несколько мест или стоячую зону.
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        После выбора можно назначить им категорию цены из списка выше.
                                    </p>
                                </div>
                            </section>
                        )}
                    </aside>

                    <section className="scheme-editor-canvas-wrap">
                        <div className="scheme-editor-canvas-toolbar">
                            <div className="scheme-editor-toolbar-tools">
                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${
                                        panelMode === "select" ? "scheme-editor-toolbar-tool-active" : ""
                                    }`}
                                    onClick={() => setPanelMode("select")}
                                >
                                    Выбор
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${
                                        panelMode === "lasso" ? "scheme-editor-toolbar-tool-active" : ""
                                    }`}
                                    onClick={() => setPanelMode("lasso")}
                                >
                                    Выбор Лассо
                                </button>

                                {hasSelectedSchemeElements ? (
                                    <button
                                        type="button"
                                        className="scheme-editor-toolbar-tool"
                                        onClick={() => {
                                            setSelectedSeatIds([]);
                                            setSelectedStandingPlaceId(null);
                                        }}
                                        title="Снять выделение со всех выбранных мест"
                                    >
                                        Сбросить выбор
                                    </button>
                                ) : null}

                                {!isPartnerTour ? (
                                    <button
                                        type="button"
                                        className={`scheme-editor-toolbar-tool ${
                                            panelMode === "price-categories" ? "scheme-editor-toolbar-tool-active" : ""
                                        }`}
                                        onClick={() => setPanelMode("price-categories")}
                                    >
                                        Категории цен
                                    </button>
                                ) : null}
                            </div>

                            <div className="scheme-editor-toolbar-tools" style={{ marginLeft: "10px" }}>
                                {!isPartnerTour && excelTemplateUrl ? (
                                    <button
                                        type="button"
                                        className="scheme-editor-toolbar-tool"
                                        onClick={() => window.open(excelTemplateUrl, "_blank", "noopener,noreferrer")}
                                        disabled={isImportingPrices}
                                    >
                                        Шаблон Excel
                                    </button>
                                ) : null}

                                {!isPartnerTour ? (
                                    <button
                                        type="button"
                                        className="scheme-editor-toolbar-tool"
                                        onClick={handleImportPricesClick}
                                        disabled={!tourCityId || isImportingPrices || isLoadingScheme}
                                    >
                                        {isImportingPrices ? "Импорт..." : "Импорт Excel"}
                                    </button>
                                ) : null}

                                {!isPartnerTour ? (
                                    <button
                                        type="button"
                                        className="scheme-editor-toolbar-tool"
                                        onClick={() => setIsManagerOpen(true)}
                                        disabled={!tourCityId || isLoadingScheme}
                                    >
                                        Адм. брони
                                    </button>
                                ) : null}

                                <input
                                    ref={importFileInputRef}
                                    type="file"
                                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    style={{ display: "none" }}
                                    onChange={handleImportPricesFileChange}
                                />
                            </div>
                            <div className="scheme-editor-toolbar-spacer" />

                            <div className="scheme-editor-scale-switch">
                                <button
                                    type="button"
                                    className={`scheme-editor-scale-button ${
                                        scale === 1
                                            ? "scheme-editor-scale-button-active"
                                            : ""
                                    }`}
                                    onClick={() => setScale(1)}
                                >
                                    1x
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-scale-button ${
                                        scale === 0.8
                                            ? "scheme-editor-scale-button-active"
                                            : ""
                                    }`}
                                    onClick={() => setScale(0.8)}
                                >
                                    0.8x
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-scale-button ${
                                        scale === 0.6
                                            ? "scheme-editor-scale-button-active"
                                            : ""
                                    }`}
                                    onClick={() => setScale(0.6)}
                                >
                                    0.6x
                                </button>
                            </div>
                        </div>

                        {isLoadingScheme ? (
                            <div className="scheme-editor-canvas-loading">
                                <div className="scheme-editor-canvas-loading-content">
                                    <h3 className="scheme-editor-canvas-loading-title">
                                        Загрузка схемы
                                    </h3>
                                    <p className="scheme-editor-canvas-loading-text">
                                        Подождите, данные схемы загружаются...
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <HallPricesCanvas
                                scale={scale}
                                scheme={scheme}
                                sectors={sectors}
                                seats={seats}
                                standingPlaces={standingPlaces}
                                selectionTool={panelMode}
                                textLabels={textLabels}
                                shapes={shapes}
                                priceCategories={priceCategories}
                                seatPrices={seatPrices as never}
                                selectedSeatIds={selectedSeatIds}
                                onSelectedSeatIdsChange={setSelectedSeatIds}
                                selectedStandingPlaceId={selectedStandingPlaceId}
                                onSelectedStandingPlaceIdChange={setSelectedStandingPlaceId}
                                onStandingPlaceOrderClick={handleStandingPlaceOrderClick}
                                blockedElementIds={blockedElementIds}
                                getBlockedInfo={getBlockedInfoForElement}
                                onBlockedHover={loadBlockedInfoForElement}
                                onSeatPricesChange={() => {
                                    // пока не используем, так как сохраняем из модалки
                                }}
                            />
                        )}
                    </section>
                </div>
            </div>

            <CreateAdminReservationModal
                isOpen={isCreateReservationOpen}
                tourCityId={tourCityId}
                selectedSeatPriceIds={selectedSeatPriceIds}
                onClose={() => setIsCreateReservationOpen(false)}
                onCreated={() => {
                    void reloadTourCityPrices();
                    setSelectedSeatIds([]);
                    setSelectedStandingPlaceId(null);
                    setSaveSuccess("Административная бронь создана.");
                }}
            />

            <StandingQuantityModal
                isOpen={isStandingQuantityOpen}
                zoneLabel={selectedStandingPlace?.text || "Стоячее место"}
                available={currentSellableStandingSeatPrice?.count ?? 0}
                isSubmitting={isSplittingStandingPrice}
                error={standingSplitError}
                onClose={() => {
                    setIsStandingQuantityOpen(false);
                    setStandingSplitError(null);
                }}
                onConfirm={(quantity) => {
                    void handleConfirmStandingQuantity(quantity);
                }}
            />

            <CreateAdminOrderModal
                isOpen={isCreateOrderOpen}
                selectedSeatPriceIds={selectedSeatPriceIds}
                selectedSeatsInfo={selectedSeatsInfo}
                tourOrganizerId={tourOrganizerId ?? null}
                isPartnerTour={isPartnerTour}
                onClose={() => {
                    setIsCreateOrderOpen(false);
                    setStandingOrderSelection(null);
                }}
                onReserved={() => {
                    // Резерв у партнёра создан, но модалка ещё открыта (показывает баннер и кнопку
                    // «Закрыть») — обновляем схему сейчас же, чтобы занятые места сразу отобразились
                    // заблокированными.
                    void reloadTourCityPrices();
                }}
                onCreated={() => {
                    void reloadTourCityPrices();
                    setSelectedSeatIds([]);
                    setSelectedStandingPlaceId(null);
                    setSaveSuccess(
                        isPartnerTour
                            ? "Резерв у партнёра создан, места заблокированы. Оплату отметьте позже в списке заказов."
                            : "Заказ создан. Найдёте его в списке заказов.",
                    );
                }}
            />

            <AdminReservationsManagerModal
                isOpen={isManagerOpen}
                tourCityId={tourCityId}
                onClose={() => setIsManagerOpen(false)}
                onChanged={() => {
                    void reloadTourCityPrices();
                }}
                seats={seats}
                standingPlaces={standingPlaces}
                sectors={sectors}
            />
        </div>
    );
};

export default HallPricesModal;
